/**
 * DWA Creative Scorecard view — Teamwork-derived metrics + Pulse scores.
 *
 * Sections:
 *  1. Header — period selector (Week/Month), count, refresh
 *  2. Current state cards — latest period's headline metrics
 *  3. Trend charts (7) — status mix, type mix, project count, lifespan,
 *     time tracking, pulse, AE workload
 *  4. Pulse form — current user submits their own weekly score
 *  5. Per-period breakdown table — mirrors the spreadsheet layout
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchScorecard,
  fetchScorecardCustom,
  fetchPulseForWeek,
  submitPulse,
  downloadScorecardPdf,
  downloadScorecardCsv,
  type ScorecardResponse,
  type ScorecardPeriod,
  type PulseScore,
} from '../services/dashboardApi';

export interface ScorecardTheme {
  pageBg: string;
  cardBg: string;
  cardBorder: string;
  cardShadow: string;
  accent: string;
  highlight?: string;
  sectionTitle: string;
  sectionDivider: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  rowDivider: string;
  positive: string;
  negative: string;
}

export interface ScorecardProps {
  theme: ScorecardTheme;
}

// ─── Color tokens used across the charts ──────────────────────────────────

const STATUS_COLORS = {
  on_track: '#15803d',     // green
  client_delay: '#dc2626',  // red
  on_hold: '#f59e0b',       // amber
  not_started: '#94a3b8',   // slate
};

const TYPE_COLORS = {
  web_development: '#0ea5e9',
  web_maintenance: '#0284c7',
  creative: '#A06820',
  mix: '#5C6B44',
  media: '#8b5cf6',
  internal: '#64748b',
};

// ─── Formatters ───────────────────────────────────────────────────────────

function fmt(n: number | null | undefined, digits = 0): string {
  if (n == null) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

function pct(n: number | null | undefined, signed = false): string {
  if (n == null) return '—';
  const v = (n * 100).toFixed(1);
  return signed && n > 0 ? `+${v}%` : `${v}%`;
}

function shortLabel(p: ScorecardPeriod, period: 'week' | 'month'): string {
  try {
    const d = new Date(p.start);
    if (period === 'week') {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short' });
  } catch {
    return p.label;
  }
}

function isoMonday(d: Date): string {
  const day = d.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + offset);
  return monday.toISOString().slice(0, 10);
}

// ─── Sub-components ───────────────────────────────────────────────────────

const MetricCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  subTone?: 'neutral' | 'positive' | 'negative';
  theme: ScorecardTheme;
}> = ({ label, value, sub, subTone = 'neutral', theme }) => {
  const subColor =
    subTone === 'positive' ? theme.positive :
    subTone === 'negative' ? theme.negative :
    theme.textMuted;
  return (
    <div style={{
      background: theme.cardBg,
      border: `1px solid ${theme.cardBorder}`,
      boxShadow: theme.cardShadow,
      borderRadius: 4,
      padding: '1.25rem 1.5rem',
    }}>
      <p style={{
        fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
        textTransform: 'uppercase', color: theme.textSecondary, marginBottom: '0.5rem',
      }}>{label}</p>
      <p style={{
        fontSize: '1.7rem', fontWeight: 700, color: theme.textPrimary, lineHeight: 1,
      }}>{value}</p>
      {sub && (
        <p style={{ fontSize: '0.78rem', color: subColor, marginTop: '0.4rem', fontWeight: 500 }}>
          {sub}
        </p>
      )}
    </div>
  );
};

const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  theme: ScorecardTheme;
}> = ({ title, subtitle, children, theme }) => (
  <div style={{
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    boxShadow: theme.cardShadow,
    borderRadius: 4,
    padding: '1rem 1.25rem 0.6rem',
  }}>
    <h3 style={{
      fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
      textTransform: 'uppercase', color: theme.sectionTitle,
      marginBottom: subtitle ? '0.15rem' : '0.75rem', paddingBottom: '0.5rem',
      borderBottom: `1px solid ${theme.sectionDivider}`,
    }}>{title}</h3>
    {subtitle && (
      <p style={{ fontSize: '0.7rem', color: theme.textMuted, marginBottom: '0.5rem' }}>
        {subtitle}
      </p>
    )}
    {children}
  </div>
);

// ─── Pulse form (self-serve weekly score) ─────────────────────────────────

interface PulseFormProps {
  weekStart: string;
  currentUserId: string | null;
  currentUserDisplayName: string | null;
  theme: ScorecardTheme;
}

const PulseForm: React.FC<PulseFormProps> = ({
  weekStart, currentUserId, currentUserDisplayName, theme,
}) => {
  const [existingScore, setExistingScore] = useState<PulseScore | null>(null);
  const [allScores, setAllScores] = useState<PulseScore[]>([]);
  const [draftScore, setDraftScore] = useState<number>(8);
  const [draftNote, setDraftNote] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const res = await fetchPulseForWeek(weekStart);
      setAllScores(res.scores);
      const mine = res.scores.find(s => s.user_id === currentUserId) || null;
      setExistingScore(mine);
      if (mine) {
        setDraftScore(mine.score);
        setDraftNote(mine.note || '');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [weekStart, currentUserId]);

  useEffect(() => { reload(); }, [reload]);

  const handleSubmit = async () => {
    if (!currentUserId) {
      setError('Sign in required to submit a pulse score.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await submitPulse({
        score: draftScore,
        week_start_monday: weekStart,
        note: draftNote || undefined,
      });
      setSuccessMsg('Score saved.');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ChartCard
      title={`This Week's Pulse — ${currentUserDisplayName || 'You'}`}
      subtitle={`Score how the week's going (1-10). Week of ${weekStart}.`}
      theme={theme}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '0.75rem', alignItems: 'center' }}>
        <input
          type="range"
          min={1}
          max={10}
          value={draftScore}
          onChange={(e) => setDraftScore(parseInt(e.target.value, 10))}
          style={{ width: 180 }}
        />
        <div style={{
          fontSize: '1.4rem',
          fontWeight: 700,
          color: draftScore >= 7 ? theme.positive : draftScore <= 4 ? theme.negative : theme.textPrimary,
        }}>
          {draftScore} / 10
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving || !currentUserId}
          style={{
            fontSize: '0.78rem', fontWeight: 600,
            padding: '0.4rem 0.85rem', borderRadius: 4,
            cursor: saving || !currentUserId ? 'default' : 'pointer',
            fontFamily: 'inherit',
            background: theme.accent, color: '#ffffff',
            border: `1px solid ${theme.accent}`,
            opacity: saving || !currentUserId ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving…' : existingScore ? 'Update' : 'Submit'}
        </button>
      </div>
      <input
        type="text"
        value={draftNote}
        onChange={(e) => setDraftNote(e.target.value)}
        placeholder="Optional note (what's driving the score)"
        style={{
          fontSize: '0.85rem', padding: '0.4rem 0.55rem', borderRadius: 4,
          border: `1px solid ${theme.cardBorder}`, marginTop: '0.5rem',
          width: '100%', boxSizing: 'border-box', fontFamily: 'inherit',
        }}
      />
      {existingScore && (
        <p style={{ fontSize: '0.75rem', color: theme.textMuted, marginTop: '0.5rem' }}>
          Submitted {new Date(existingScore.submitted_at || '').toLocaleString()}.
        </p>
      )}
      {error && <p style={{ color: theme.negative, fontSize: '0.78rem', marginTop: '0.4rem' }}>{error}</p>}
      {successMsg && <p style={{ color: theme.positive, fontSize: '0.78rem', marginTop: '0.4rem' }}>{successMsg}</p>}

      {/* Other employees' scores this week — view-only */}
      {allScores.length > 1 && (
        <div style={{ marginTop: '1rem', borderTop: `1px solid ${theme.rowDivider}`, paddingTop: '0.75rem' }}>
          <p style={{
            fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: theme.textSecondary, marginBottom: '0.5rem',
          }}>Team this week</p>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {allScores
              .filter(s => s.user_id !== currentUserId)
              .map(s => (
                <li key={s.id} style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: '0.35rem 0', fontSize: '0.85rem',
                  borderBottom: `1px solid ${theme.rowDivider}`,
                }}>
                  <span style={{ color: theme.textPrimary }}>{s.display_name}</span>
                  <span style={{
                    color: s.score >= 7 ? theme.positive : s.score <= 4 ? theme.negative : theme.textPrimary,
                    fontWeight: 600,
                  }}>
                    {s.score} / 10
                  </span>
                </li>
              ))}
          </ul>
        </div>
      )}
    </ChartCard>
  );
};

// ─── Main view ────────────────────────────────────────────────────────────

type ScorecardView = 'weekly' | 'monthly' | 'yoy' | 'custom';

function defaultCustomRange(): { startDate: string; endDate: string } {
  // Default to the last completed calendar month
  const now = new Date();
  const firstOfThis = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastOfPrev = new Date(firstOfThis.getTime() - 86400_000);
  const firstOfPrev = new Date(lastOfPrev.getFullYear(), lastOfPrev.getMonth(), 1);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { startDate: fmt(firstOfPrev), endDate: fmt(lastOfPrev) };
}

export const ScorecardView: React.FC<ScorecardProps> = ({ theme }) => {
  const { user } = useAuth();
  const [view, setView] = useState<ScorecardView>('weekly');
  // Default counts per view; ignored for custom.
  const count = view === 'monthly' || view === 'yoy' ? 6 : 5;
  const [customRange, setCustomRange] = useState(defaultCustomRange);
  const [pendingCustomRange, setPendingCustomRange] = useState(customRange);
  const [data, setData] = useState<ScorecardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingKind, setDownloadingKind] = useState<'pdf' | 'csv' | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  // Weekly / Monthly normally end at the last *completed* period; this adds the
  // current one so far (Jon, 2026-11-03: "no way to see the current period").
  const [includeCurrent, setIncludeCurrent] = useState(false);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);

  // Derived: which period the underlying API call uses.
  const apiPeriod: 'week' | 'month' = view === 'weekly' ? 'week' : 'month';

  const handleDownload = useCallback(async (kind: 'pdf' | 'csv') => {
    setDownloadingKind(kind);
    setDownloadError(null);
    try {
      if (kind === 'pdf') {
        await downloadScorecardPdf({ period: apiPeriod, count });
      } else {
        await downloadScorecardCsv({ period: apiPeriod, count });
      }
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloadingKind(null);
    }
  }, [apiPeriod, count]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let res: ScorecardResponse;
      if (view === 'custom') {
        res = await fetchScorecardCustom(customRange);
      } else if (view === 'yoy') {
        res = await fetchScorecard({ period: 'month', count, compare: 'yoy' });
      } else {
        res = await fetchScorecard({
          period: apiPeriod,
          count,
          ...(includeCurrent ? { referenceDate: new Date().toISOString() } : {}),
        });
      }
      setData(res);
      setLoadedAt(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [view, apiPeriod, count, customRange, includeCurrent]);

  useEffect(() => { load(); }, [load]);

  const showsCurrent = includeCurrent && (view === 'weekly' || view === 'monthly');

  // Derived chart data — periods come newest-first; charts read better
  // oldest-first, so reverse for the chart series. When compare='yoy',
  // each x-axis label gets parallel prior-year values under `*_py` keys
  // so charts can render a side-by-side overlay.
  // 'custom' API uses a single window — fall back to the weekly label.
  const chartSeries = useMemo(() => {
    if (!data) return [];
    const labelPeriod: 'week' | 'month' = data.period === 'custom' ? 'week' : data.period;
    const cur = [...data.periods].reverse();
    const py = data.yoy_periods ? [...data.yoy_periods].reverse() : [];
    return cur.map((p, idx) => {
      const pyP = py[idx];
      return {
        label: shortLabel(p, labelPeriod),
        total_projects: p.metrics.total_projects,
        on_track_pct: p.metrics.status_pct.on_track * 100,
        client_delay_pct: p.metrics.status_pct.client_delay * 100,
        on_hold_pct: p.metrics.status_pct.on_hold * 100,
        not_started_pct: p.metrics.status_pct.not_started * 100,
        web_development: p.metrics.type_counts.web_development,
        web_maintenance: p.metrics.type_counts.web_maintenance,
        creative: p.metrics.type_counts.creative,
        mix: p.metrics.type_counts.mix,
        media: p.metrics.type_counts.media,
        internal: p.metrics.type_counts.internal,
        avg_lifespan_days: p.metrics.avg_lifespan_days,
        time_logged: p.metrics.time_logged,
        available_hours: p.metrics.available_hours,
        pulse_avg: p.metrics.pulse.average,
        // Prior-year overlay fields (null when no YoY data for this index)
        total_projects_py: pyP?.metrics.total_projects ?? null,
        avg_lifespan_days_py: pyP?.metrics.avg_lifespan_days ?? null,
        time_logged_py: pyP?.metrics.time_logged ?? null,
        available_hours_py: pyP?.metrics.available_hours ?? null,
        pulse_avg_py: pyP?.metrics.pulse.average ?? null,
      };
    });
  }, [data]);

  const showYoY = !!(data?.compare === 'yoy' && data?.yoy_periods && data.yoy_periods.length > 0);

  const latest = data?.periods[0]; // newest first in API
  const aeData = latest?.metrics.by_account_executive ?? [];
  const weekStartMonday = useMemo(() => isoMonday(new Date()), []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Header / period selector */}
      <div style={{
        background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
        boxShadow: theme.cardShadow, borderRadius: 4, padding: '1rem 1.5rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem',
      }}>
        <div>
          <p style={{
            fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase',
            color: theme.textSecondary, fontWeight: 600, marginBottom: '0.35rem',
          }}>DWA Creative Scorecard</p>
          <p style={{ fontSize: '0.85rem', color: theme.textPrimary, margin: 0 }}>
            {latest ? `${showsCurrent ? 'Current' : 'Latest'}: ${latest.label}${showsCurrent ? ' (so far)' : ''}` : 'Loading…'}
            {data && data.periods.length > 1 && (
              <span style={{ color: theme.textMuted }}> · {data.count} periods</span>
            )}
            {loadedAt && (
              <span style={{ color: theme.textMuted }}>
                {' '}· refreshed {loadedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {([
            ['weekly', 'Weekly'],
            ['monthly', 'Monthly'],
            ['yoy', 'YoY'],
            ['custom', 'Custom'],
          ] as const).map(([v, label]) => {
            const active = view === v;
            return (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{
                  fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.04em',
                  padding: '0.4rem 0.95rem', borderRadius: 3, cursor: 'pointer',
                  fontFamily: 'inherit',
                  background: active ? theme.sectionTitle : 'transparent',
                  color: active ? '#ffffff' : theme.textSecondary,
                  border: `1px solid ${active ? theme.sectionTitle : theme.cardBorder}`,
                }}
              >
                {label}
              </button>
            );
          })}
          {view === 'custom' && (
            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', marginLeft: '0.5rem' }}>
              <input
                type="date"
                value={pendingCustomRange.startDate}
                onChange={(e) => setPendingCustomRange((p) => ({ ...p, startDate: e.target.value }))}
                style={{
                  fontSize: '0.78rem', padding: '0.35rem 0.5rem', borderRadius: 4,
                  border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
                  color: theme.textPrimary, fontFamily: 'inherit',
                }}
              />
              <span style={{ color: theme.textMuted, fontSize: '0.78rem' }}>→</span>
              <input
                type="date"
                value={pendingCustomRange.endDate}
                onChange={(e) => setPendingCustomRange((p) => ({ ...p, endDate: e.target.value }))}
                style={{
                  fontSize: '0.78rem', padding: '0.35rem 0.5rem', borderRadius: 4,
                  border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
                  color: theme.textPrimary, fontFamily: 'inherit',
                }}
              />
              <button
                type="button"
                onClick={() => setCustomRange(pendingCustomRange)}
                disabled={
                  !pendingCustomRange.startDate ||
                  !pendingCustomRange.endDate ||
                  pendingCustomRange.startDate > pendingCustomRange.endDate
                }
                style={{
                  fontSize: '0.78rem', fontWeight: 600,
                  padding: '0.35rem 0.85rem', borderRadius: 4, cursor: 'pointer',
                  fontFamily: 'inherit',
                  background: theme.accent, color: '#1a202c',
                  border: `1px solid ${theme.accent}`,
                }}
              >
                Load
              </button>
            </div>
          )}
          {(view === 'weekly' || view === 'monthly') && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.78rem', color: theme.textSecondary, marginLeft: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={includeCurrent} onChange={(e) => setIncludeCurrent(e.target.checked)} />
              Include this {view === 'weekly' ? 'week' : 'month'} so far
            </label>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            title="Reload the scorecard (Teamwork data updates daily)"
            style={{
              fontSize: '0.78rem', fontWeight: 600,
              padding: '0.4rem 0.95rem', borderRadius: 4,
              cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit',
              background: 'transparent', color: theme.textPrimary,
              border: `1px solid ${theme.cardBorder}`, whiteSpace: 'nowrap', marginLeft: '0.5rem',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Refreshing…' : '↻ Refresh'}
          </button>
          <button
            type="button"
            onClick={() => handleDownload('pdf')}
            disabled={downloadingKind !== null || !data}
            style={{
              fontSize: '0.78rem', fontWeight: 600,
              padding: '0.4rem 0.95rem', borderRadius: 4,
              cursor: downloadingKind !== null || !data ? 'default' : 'pointer',
              fontFamily: 'inherit',
              background: theme.accent, color: '#1a202c',
              border: `1px solid ${theme.accent}`,
              opacity: downloadingKind !== null || !data ? 0.6 : 1, whiteSpace: 'nowrap',
              marginLeft: '0.5rem',
            }}
          >
            {downloadingKind === 'pdf' ? 'Generating…' : '⬇ PDF'}
          </button>
          <button
            type="button"
            onClick={() => handleDownload('csv')}
            disabled={downloadingKind !== null || !data}
            style={{
              fontSize: '0.78rem', fontWeight: 600,
              padding: '0.4rem 0.95rem', borderRadius: 4,
              cursor: downloadingKind !== null || !data ? 'default' : 'pointer',
              fontFamily: 'inherit',
              background: 'transparent', color: theme.textPrimary,
              border: `1px solid ${theme.cardBorder}`,
              opacity: downloadingKind !== null || !data ? 0.6 : 1, whiteSpace: 'nowrap',
            }}
          >
            {downloadingKind === 'csv' ? 'Generating…' : '⬇ CSV'}
          </button>
        </div>
      </div>
      {downloadError && (
        <p style={{ color: theme.negative, fontSize: '0.78rem', padding: '0 1.5rem' }}>
          {downloadError}
        </p>
      )}

      {loading && !data && (
        <p style={{ color: theme.textMuted, padding: '2rem' }}>Loading scorecard…</p>
      )}
      {error && (
        <p style={{ color: theme.negative, padding: '1rem 1.5rem' }}>Error: {error}</p>
      )}

      {data && (
        <>
          {/* ─── Current state cards (from latest period) ───────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <MetricCard
              label="Total Client Projects"
              value={fmt(latest?.metrics.total_projects)}
              sub={`${latest?.label || ''}`}
              theme={theme}
            />
            <MetricCard
              label="On Track %"
              value={pct(latest?.metrics.status_pct.on_track)}
              sub={`${fmt(latest?.metrics.status_counts.on_track)} projects`}
              subTone="positive"
              theme={theme}
            />
            <MetricCard
              label="Client Delay %"
              value={pct(latest?.metrics.status_pct.client_delay)}
              sub={`${fmt(latest?.metrics.status_counts.client_delay)} projects`}
              subTone={latest && latest.metrics.status_pct.client_delay > 0.05 ? 'negative' : 'neutral'}
              theme={theme}
            />
            <MetricCard
              label="Avg Project Lifespan"
              value={`${fmt(latest?.metrics.avg_lifespan_days, 1)}d`}
              sub="open projects"
              theme={theme}
            />
            <MetricCard
              label="Team Pulse"
              value={latest?.metrics.pulse.average != null ? `${latest.metrics.pulse.average.toFixed(1)} / 10` : '—'}
              sub={`${latest?.metrics.pulse.by_employee.length ?? 0} responses`}
              subTone={
                latest?.metrics.pulse.average != null
                  ? latest.metrics.pulse.average >= 7
                    ? 'positive'
                    : latest.metrics.pulse.average <= 5
                      ? 'negative'
                      : 'neutral'
                  : 'neutral'
              }
              theme={theme}
            />
            <MetricCard
              label="Time Logged"
              value={`${fmt(latest?.metrics.time_logged, 1)}h`}
              sub={`vs ${fmt(latest?.metrics.available_hours, 0)}h available`}
              subTone={
                latest && latest.metrics.time_difference < -5
                  ? 'negative'
                  : latest && latest.metrics.time_difference > 0
                    ? 'positive'
                    : 'neutral'
              }
              theme={theme}
            />
          </div>

          {/* ─── Charts (7) ────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '1rem' }}>
            <ChartCard
              title="Project Status Mix"
              subtitle={`% of all client projects per ${apiPeriod}, stacked.`}
              theme={theme}
            >
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartSeries} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
                  <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" stroke={theme.textMuted} fontSize={11} />
                  <YAxis stroke={theme.textMuted} fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v) => `${(v as number).toFixed(1)}%`} />
                  <Legend wrapperStyle={{ fontSize: '0.72rem' }} />
                  <Bar dataKey="on_track_pct" name="On Track" stackId="s" fill={STATUS_COLORS.on_track} />
                  <Bar dataKey="client_delay_pct" name="Client Delay" stackId="s" fill={STATUS_COLORS.client_delay} />
                  <Bar dataKey="on_hold_pct" name="On Hold" stackId="s" fill={STATUS_COLORS.on_hold} />
                  <Bar dataKey="not_started_pct" name="Not Started" stackId="s" fill={STATUS_COLORS.not_started} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Project Type Mix"
              subtitle="Counts per type per period."
              theme={theme}
            >
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartSeries} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
                  <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" stroke={theme.textMuted} fontSize={11} />
                  <YAxis stroke={theme.textMuted} fontSize={11} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: '0.72rem' }} />
                  <Bar dataKey="web_development" name="Web Dev" stackId="t" fill={TYPE_COLORS.web_development} />
                  <Bar dataKey="web_maintenance" name="Web Maint" stackId="t" fill={TYPE_COLORS.web_maintenance} />
                  <Bar dataKey="creative" name="Creative" stackId="t" fill={TYPE_COLORS.creative} />
                  <Bar dataKey="mix" name="Mix" stackId="t" fill={TYPE_COLORS.mix} />
                  <Bar dataKey="media" name="Media" stackId="t" fill={TYPE_COLORS.media} />
                  <Bar dataKey="internal" name="Internal" stackId="t" fill={TYPE_COLORS.internal} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Total Open Projects" subtitle={showYoY ? 'Solid: current · Dashed: same period last year.' : undefined} theme={theme}>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartSeries} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
                  <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" stroke={theme.textMuted} fontSize={11} />
                  <YAxis stroke={theme.textMuted} fontSize={11} />
                  <Tooltip />
                  {showYoY && <Legend wrapperStyle={{ fontSize: '0.72rem' }} />}
                  <Line type="monotone" dataKey="total_projects" name="Current" stroke={theme.accent} strokeWidth={2} dot={{ r: 3 }} />
                  {showYoY && (
                    <Line type="monotone" dataKey="total_projects_py" name="Prior Year" stroke={theme.textMuted} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 2 }} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Avg Open Project Lifespan (days)" subtitle={showYoY ? 'Solid: current · Dashed: same period last year.' : undefined} theme={theme}>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartSeries} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
                  <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" stroke={theme.textMuted} fontSize={11} />
                  <YAxis stroke={theme.textMuted} fontSize={11} />
                  <Tooltip />
                  {showYoY && <Legend wrapperStyle={{ fontSize: '0.72rem' }} />}
                  <Line type="monotone" dataKey="avg_lifespan_days" name="Current" stroke={theme.highlight || theme.accent} strokeWidth={2} dot={{ r: 3 }} />
                  {showYoY && (
                    <Line type="monotone" dataKey="avg_lifespan_days_py" name="Prior Year" stroke={theme.textMuted} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 2 }} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Time Logged vs Available"
              subtitle={
                showYoY
                  ? 'Solid: current · Dashed prior-year: lighter dotted overlay.'
                  : 'Both in hours per period; available = workdays × 7 × team size.'
              }
              theme={theme}
            >
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartSeries} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
                  <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" stroke={theme.textMuted} fontSize={11} />
                  <YAxis stroke={theme.textMuted} fontSize={11} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: '0.72rem' }} />
                  <Line type="monotone" dataKey="time_logged" name="Logged" stroke={theme.accent} strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="available_hours" name="Available" stroke={theme.textMuted} strokeWidth={2} strokeDasharray="4 4" dot={false} />
                  {showYoY && (
                    <Line type="monotone" dataKey="time_logged_py" name="Logged (PY)" stroke={theme.accent} strokeWidth={1.5} strokeDasharray="2 2" dot={{ r: 2 }} opacity={0.55} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Team Pulse (avg)" subtitle={showYoY ? 'Solid: current · Dashed: same period last year.' : '1-10 scale.'} theme={theme}>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartSeries} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
                  <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" stroke={theme.textMuted} fontSize={11} />
                  <YAxis stroke={theme.textMuted} fontSize={11} domain={[0, 10]} />
                  <Tooltip />
                  {showYoY && <Legend wrapperStyle={{ fontSize: '0.72rem' }} />}
                  <Line type="monotone" dataKey="pulse_avg" name="Current" stroke={theme.positive} strokeWidth={2} dot={{ r: 4 }} />
                  {showYoY && (
                    <Line type="monotone" dataKey="pulse_avg_py" name="Prior Year" stroke={theme.textMuted} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="AE Workload — Current Period"
              subtitle="Projects per Account Executive (project owner)."
              theme={theme}
            >
              <ResponsiveContainer width="100%" height={Math.max(180, aeData.length * 32 + 40)}>
                <BarChart
                  data={aeData}
                  layout="vertical"
                  margin={{ top: 8, right: 24, left: 8, bottom: 4 }}
                >
                  <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" stroke={theme.textMuted} fontSize={11} />
                  <YAxis dataKey="name" type="category" stroke={theme.textMuted} fontSize={11} width={140} />
                  <Tooltip />
                  <Bar dataKey="count" fill={theme.accent}>
                    {aeData.map((_, i) => (
                      <Cell key={i} fill={theme.accent} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* ─── Pulse form ──────────────────────────────────────────────── */}
          <PulseForm
            weekStart={weekStartMonday}
            currentUserId={user?.id ?? null}
            currentUserDisplayName={user?.display_name ?? user?.email ?? null}
            theme={theme}
          />

          {/* ─── Per-period breakdown table ──────────────────────────────── */}
          <div style={{
            background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
            boxShadow: theme.cardShadow, borderRadius: 4, padding: '1.25rem 1.5rem',
          }}>
            <h3 style={{
              fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
              textTransform: 'uppercase', color: theme.sectionTitle,
              marginBottom: '1rem', paddingBottom: '0.5rem',
              borderBottom: `1px solid ${theme.sectionDivider}`,
            }}>Per-Period Breakdown</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${theme.sectionDivider}` }}>
                    <th style={th(theme, 'left')}>Period</th>
                    <th style={th(theme)}>Total</th>
                    <th style={th(theme)}>On Track</th>
                    <th style={th(theme)}>Client Delay</th>
                    <th style={th(theme)}>On Hold</th>
                    <th style={th(theme)}>Not Started</th>
                    <th style={th(theme)}>Avg Lifespan (d)</th>
                    <th style={th(theme)}>Time Logged</th>
                    <th style={th(theme)}>Available</th>
                    <th style={th(theme)}>Δ Hours</th>
                    <th style={th(theme)}>Pulse</th>
                  </tr>
                </thead>
                <tbody>
                  {data.periods.map((p) => (
                    <tr key={p.start} style={{ borderBottom: `1px solid ${theme.rowDivider}` }}>
                      <td style={td(theme, 'left')}>{p.label}</td>
                      <td style={td(theme)}>{p.metrics.total_projects}</td>
                      <td style={td(theme)}>{p.metrics.status_counts.on_track} · {pct(p.metrics.status_pct.on_track)}</td>
                      <td style={td(theme)}>{p.metrics.status_counts.client_delay} · {pct(p.metrics.status_pct.client_delay)}</td>
                      <td style={td(theme)}>{p.metrics.status_counts.on_hold}</td>
                      <td style={td(theme)}>{p.metrics.status_counts.not_started}</td>
                      <td style={td(theme)}>{p.metrics.avg_lifespan_days.toFixed(1)}</td>
                      <td style={td(theme)}>{p.metrics.time_logged.toFixed(1)}h</td>
                      <td style={td(theme)}>{p.metrics.available_hours}h</td>
                      <td style={{
                        ...td(theme),
                        color: p.metrics.time_difference < 0 ? theme.negative : theme.positive,
                        fontWeight: 600,
                      }}>
                        {p.metrics.time_difference > 0 ? '+' : ''}{p.metrics.time_difference.toFixed(1)}h
                      </td>
                      <td style={td(theme)}>
                        {p.metrics.pulse.average != null ? p.metrics.pulse.average.toFixed(1) : '—'}
                      </td>
                    </tr>
                  ))}
                  {showYoY && data.yoy_periods && data.yoy_periods.length > 0 && (
                    <>
                      <tr style={{ borderTop: `2px solid ${theme.sectionDivider}` }}>
                        <td colSpan={11} style={{
                          ...td(theme, 'left'),
                          padding: '0.6rem 0.4rem 0.3rem',
                          fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase',
                          color: theme.sectionTitle, fontWeight: 700,
                        }}>
                          Prior Year (same periods, 1 year back)
                        </td>
                      </tr>
                      {data.yoy_periods.map((p) => (
                        <tr key={`py-${p.start}`} style={{ borderBottom: `1px solid ${theme.rowDivider}`, color: theme.textMuted }}>
                          <td style={td(theme, 'left')}>{p.label}</td>
                          <td style={td(theme)}>{p.metrics.total_projects}</td>
                          <td style={td(theme)}>{p.metrics.status_counts.on_track} · {pct(p.metrics.status_pct.on_track)}</td>
                          <td style={td(theme)}>{p.metrics.status_counts.client_delay} · {pct(p.metrics.status_pct.client_delay)}</td>
                          <td style={td(theme)}>{p.metrics.status_counts.on_hold}</td>
                          <td style={td(theme)}>{p.metrics.status_counts.not_started}</td>
                          <td style={td(theme)}>{p.metrics.avg_lifespan_days.toFixed(1)}</td>
                          <td style={td(theme)}>{p.metrics.time_logged.toFixed(1)}h</td>
                          <td style={td(theme)}>{p.metrics.available_hours}h</td>
                          <td style={td(theme)}>
                            {p.metrics.time_difference > 0 ? '+' : ''}{p.metrics.time_difference.toFixed(1)}h
                          </td>
                          <td style={td(theme)}>
                            {p.metrics.pulse.average != null ? p.metrics.pulse.average.toFixed(1) : '—'}
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                  {data.rollup && (
                    <tr style={{ borderTop: `2px solid ${theme.sectionDivider}`, fontWeight: 600 }}>
                      <td style={td(theme, 'left')}>Average</td>
                      <td style={td(theme)}>{data.rollup.total_projects.toFixed(1)}</td>
                      <td style={td(theme)}>{data.rollup.status_counts.on_track.toFixed(1)} · {pct(data.rollup.status_pct.on_track)}</td>
                      <td style={td(theme)}>{data.rollup.status_counts.client_delay.toFixed(1)} · {pct(data.rollup.status_pct.client_delay)}</td>
                      <td style={td(theme)}>{data.rollup.status_counts.on_hold.toFixed(1)}</td>
                      <td style={td(theme)}>{data.rollup.status_counts.not_started.toFixed(1)}</td>
                      <td style={td(theme)}>{data.rollup.avg_lifespan_days.toFixed(1)}</td>
                      <td style={td(theme)}>{data.rollup.time_logged.toFixed(1)}h</td>
                      <td style={td(theme)}>{data.rollup.available_hours.toFixed(1)}h</td>
                      <td style={td(theme)}>{data.rollup.time_difference > 0 ? '+' : ''}{data.rollup.time_difference.toFixed(1)}h</td>
                      <td style={td(theme)}>{data.rollup.pulse.average != null ? data.rollup.pulse.average.toFixed(1) : '—'}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Table style helpers ──────────────────────────────────────────────────

function th(theme: ScorecardTheme, align: 'left' | 'right' = 'right'): React.CSSProperties {
  return {
    padding: '0.5rem 0.5rem',
    textAlign: align,
    fontSize: '0.7rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.textSecondary,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
}

function td(theme: ScorecardTheme, align: 'left' | 'right' = 'right'): React.CSSProperties {
  return {
    padding: '0.45rem 0.5rem',
    textAlign: align,
    color: theme.textPrimary,
    whiteSpace: 'nowrap',
  };
}

export default ScorecardView;
